import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Plus } from "lucide-react";
import { useDocHubTags } from "@/hooks/useDocHubTags";

interface DocumentationTagSelectorProps {
  brandId: string;
  selectedTagIds: string[];
  onTagsChange: (tagIds: string[]) => void;
  className?: string;
}

export function DocumentationTagSelector({
  brandId,
  selectedTagIds,
  onTagsChange,
  className = "",
}: DocumentationTagSelectorProps) {
  const { tags, createTag, isCreating } = useDocHubTags(brandId);
  const [newTagName, setNewTagName] = useState("");

  const selectedTags = tags.filter((tag) => selectedTagIds.includes(tag.id));
  const availableTags = tags.filter((tag) => !selectedTagIds.includes(tag.id));

  const toggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onTagsChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onTagsChange([...selectedTagIds, tagId]);
    }
  };

  const removeTag = (tagId: string) => {
    onTagsChange(selectedTagIds.filter((id) => id !== tagId));
  };

  const handleCreateTag = () => {
    if (!newTagName.trim()) return;

    const randomColor = [
      "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
      "#ec4899", "#14b8a6", "#f97316", "#06b6d4", "#6366f1"
    ][Math.floor(Math.random() * 10)];

    createTag({
      brand_id: brandId,
      name: newTagName.trim(),
      color: randomColor,
      level: 0,
      sort_order: 0,
      parent_id: null,
    });

    setNewTagName("");
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium">Selected Tags</label>
          <div className="flex flex-wrap gap-2">
            {selectedTags.map((tag) => (
              <Badge
                key={tag.id}
                style={{ backgroundColor: tag.color }}
                className="text-white pr-1"
              >
                {tag.name}
                <button
                  onClick={() => removeTag(tag.id)}
                  className="ml-1 hover:bg-black/20 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Available Tags */}
      {availableTags.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-medium">Available Tags</label>
          <div className="flex flex-wrap gap-2">
            {availableTags.map((tag) => (
              <Badge
                key={tag.id}
                variant="outline"
                className="cursor-pointer hover:opacity-80 active:scale-95 transition-all"
                style={{ 
                  borderColor: tag.color, 
                  color: tag.color,
                  borderWidth: '2px'
                }}
                onClick={() => toggleTag(tag.id)}
              >
                {tag.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Create New Tag */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Add New Tag</label>
        <div className="flex gap-2">
          <Input
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            placeholder="Tag name"
            onKeyDown={(e) => e.key === 'Enter' && handleCreateTag()}
            disabled={isCreating}
          />
          <Button
            onClick={handleCreateTag}
            disabled={!newTagName.trim() || isCreating}
            size="icon"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
